import { ContentDelivery, useContentDeliveryAPI, useEpiserver } from '@episerver/spa-core';
import { SubMenuDto } from './types';
import { OldDomainPageType } from '../../../../Models/Content/OldDomainPageData';
import { useEffect, useState } from 'react';
import IContent from '@episerver/spa-core/dist/Models/IContent';
import { GoToTopAnchor } from './GoToTopAnchor';
import { SkipToContent } from './SkipToContent';
import OldBreadcrumbs from './OldBreadcrumbs';
import { GoToTopLink } from './GoToTopLink';
import OldFeedback from './OldFeedback';
import OldFooter from './OldFooter';
import MainMenu from './MainMenu';
import SiteTop from './SiteTop';
import axios from 'axios';
import { ArticlePageType } from 'Models/ArticlePageData';
import { SiteMapPageType } from 'Models/SiteMapPageData';
import { OldSearchPageType } from 'Models/OldSearchPageData';
import { DummyPageType } from 'Models/DummyPageData';
import { TransportPageType } from 'Models/Content/TransportPageData';
import { OldBerammingSokPageType } from 'Models/OldBerammingSokPageData';
import { TagPageType } from 'Models/TagPageData';
import { RootLayoutProps } from '../../../Layout/RootLayoutProps';
import { General, useTranslation } from 'app/hooks/useTranslations';
import MetaTags from './MetaTags';

function ShouldHideDidYouFind(currentContent: CurrentContent): boolean {
    if ('hideDidYouFind' in currentContent) {
        return currentContent.hideDidYouFind.value;
    }
    return true;
}

// Every possible OldDomstol Page Types
export type CurrentContent =
    | OldDomainPageType
    | ArticlePageType
    | OldSearchPageType
    | SiteMapPageType
    | DummyPageType
    | TransportPageType
    | OldBerammingSokPageType
    | TagPageType;

const apiUrl = '/api/episerver/v3/mainmenu';

export const HoyesterettLayout = (
    props: RootLayoutProps & {
        currentContent: CurrentContent;
        layoutSettings?: ContentDelivery.StringProperty;
        pathname?: string;
        children?: React.ReactNode;
    }
) => {
    const { currentLanguage, currentContent, layoutSettings } = props;
    const { t } = useTranslation();
    const api = useContentDeliveryAPI();
    const ctx = useEpiserver();
    const [domainPage, setDomainPage] = useState<OldDomainPageType>();
    const [mainMenu, setMainMenu] = useState<SubMenuDto[]>();
    const context = useEpiserver();

    const language = currentContent?.language?.name ?? 'no';

    function getPageUrlFromEpiserverEditMode(url: string): string {
        const match = url.match(/Content\/(.*?),,/);
        if (match && match.length > 1 && match[1]) {
            return match[1];
        } else {
            return 'no/hoyesterett';
        }
    }

    useEffect(() => {
        const updateMenu = (domainPage: OldDomainPageType) => {
            if (domainPage.cssBodyClass?.value) {
                document.body.classList.add(domainPage.cssBodyClass?.value);
            }

            const url = new URL(domainPage?.contentLink?.url).pathname;
            const axiosUrl = `${apiUrl}?url=${ctx.isInEditMode ? getPageUrlFromEpiserverEditMode(url) : url}&language=${domainPage.language?.name}`;
            axios.get<SubMenuDto[]>(axiosUrl).then((childrenList) => {
                setMainMenu(childrenList.data);
            });
        };

        const getDomainPageFromAncestors = (currentContent: IContent) => {
            api.Language = currentContent.language?.name ?? 'no';
            api.getAncestors(currentContent).then((ancestors) => {
                const currentDomainPage = ancestors.find((ancestor) => ancestor.contentType.includes('OldDomainPage'));

                if (currentDomainPage) {
                    setDomainPage(currentDomainPage as OldDomainPageType);
                    updateMenu(currentDomainPage as OldDomainPageType);
                }
            });
        };

        if (props.currentContent?.contentType.includes('OldDomainPage')) {
            setDomainPage(props.currentContent as OldDomainPageType);
            updateMenu(props.currentContent as OldDomainPageType);
        } else if (props.currentContent && (!domainPage || domainPage.language?.name !== language)) {
            getDomainPageFromAncestors(props.currentContent);
        }
    }, [api, domainPage, language, props.currentContent]);

    if (!domainPage || !mainMenu) {
        return null;
    }

    const hideDidYouFind = ShouldHideDidYouFind(props.currentContent);
    const norwegianCourtsLocaleText = t(`${General}.NorwegianCourts`, language);

    return (
        <>
            <SkipToContent language={currentLanguage ?? 'no'} />
            <GoToTopAnchor />
            <div
                className="page-wrapper old hoyesterett"
                style={context.isServerSideRendering() ? { visibility: 'hidden' } : {}}
            >
                <MetaTags
                    currentContent={currentContent}
                    currentLanguage={currentLanguage}
                    norwegianCourtsLocaleText={norwegianCourtsLocaleText}
                />
                <header className="page-header">
                    <div className="container">
                        <SiteTop domainPage={domainPage} currentPage={currentContent} />
                        <MainMenu domainPage={domainPage} currentPage={currentContent} menu={mainMenu} />
                    </div>
                    <OldBreadcrumbs currentPage={currentContent} />
                </header>

                <main className="container" id="main-content" tabIndex={-1}>
                    {props.children}
                </main>

                {!hideDidYouFind && (
                    <div className="layout-row">
                        <OldFeedback currentPage={currentContent} layoutSettings={layoutSettings} />
                    </div>
                )}

                {domainPage && <OldFooter domainPage={domainPage} />}
            </div>
            <GoToTopLink />
        </>
    );
};

export default HoyesterettLayout;
